import { useState } from 'react';
import { useGetUserProfile } from './use-get-user-profile.hook';
import { useGetCaregiverStatus } from './use-get-caregiver-status.hook';

export type OnboardingStep =
  | 'highlights'
  | 'account-setup'
  | 'consent'
  | 'caregiver-permissions';

const CAREGIVER_ONBOARDING_COMPLETE_FIELD = 'caregiver_onboarding_complete';

export const useOnboarding = () => {
  const [completionError, setCompletionError] = useState<Error | null>(null);
  const {
    data,
    isLoading: isProfileLoading,
    isError,
    refetch,
  } = useGetUserProfile();
  const {
    data: caregiverStatus,
    isLoading: isCaregiverLoading,
    isError: isCaregiverError,
    refetch: refetchCaregiverStatus,
    isImpersonationStarted,
  } = useGetCaregiverStatus();
  const [step, setStep] = useState<OnboardingStep>('highlights');

  const isLoading = isProfileLoading || isCaregiverLoading;
  const isCaregiverUser = Boolean(caregiverStatus);
  const caregiverOnboardingComplete =
    data?.custom_fields?.find(
      (f) => f.name === CAREGIVER_ONBOARDING_COMPLETE_FIELD,
    )?.value === 'true';
  // A care recipient the caregiver is impersonating may never have opened the app, so their
  // profile still reads ftux_complete: false — the same state as a member who has not onboarded
  // yet. Onboarding ends in a consent step, which the caregiver must not answer on their behalf;
  // it waits until that person logs in themselves (TRIL-943).
  const shouldShowOnboarding =
    !isImpersonationStarted &&
    (isCaregiverUser
      ? !caregiverOnboardingComplete
      : data?.ftux_complete === false);
  const hasFetchError = isError || isCaregiverError;
  const hasError = hasFetchError || completionError !== null;

  const retry = hasFetchError
    ? () => {
        if (isError) refetch();
        if (isCaregiverError) refetchCaregiverStatus();
      }
    : () => window.location.reload();

  const onOnboardingComplete = (error?: Error) => {
    if (error) {
      setCompletionError(error);
      return;
    }
    setStep('account-setup');
  };

  const onAccountSetupComplete = () => setStep('consent');

  const onConsentComplete = () => setStep('caregiver-permissions');

  return {
    shouldShowOnboarding,
    isLoading,
    hasError,
    retry,
    step,
    isCaregiver: isCaregiverUser,
    onOnboardingComplete,
    onAccountSetupComplete,
    onConsentComplete,
  };
};
